import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  formatQuantidadeComUnidade,
  formatTotaisPorUnidade,
  toListaPorUnidade
} from './numberFormat';

const BRAND = {
  navy: [8, 24, 96],
  blue: [24, 48, 176],
  light: [244, 247, 255],
  gray: [75, 85, 99],
  dark: [8, 24, 96],
  line: [197, 208, 248]
};

const REPORT_META = {
  geral: {
    title: 'Relatório Gerencial',
    subtitle: 'Visão consolidada de qualidade, fornecedores e recebimentos'
  },
  fornecedores: {
    title: 'Relatório de Fornecedores',
    subtitle: 'Status, distribuição e desempenho da base de fornecedores'
  },
  produtos: {
    title: 'Relatório de Produtos',
    subtitle: 'Catálogo de insumos e matérias-primas da empresa'
  },
  documentos: {
    title: 'Relatório de Documentos',
    subtitle: 'Conformidade documental e status no período'
  },
  recebimentos: {
    title: 'Relatório de Recebimentos',
    subtitle: 'Análise detalhada dos recebimentos no período selecionado'
  },
  marketplace: {
    title: 'Relatório de Marketplace',
    subtitle: 'Propostas e movimentação do marketplace'
  }
};

function formatDateBr(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd || '-';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

function toJsDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value?.toDate === 'function') {
    try {
      const d = value.toDate();
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }
  // Timestamp serializado pelo Callable ({ _seconds } ou { seconds })
  if (typeof value === 'object') {
    const sec = value._seconds ?? value.seconds;
    if (sec != null) {
      const d = new Date(Number(sec) * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      const d = new Date(`${trimmed.slice(0, 10)}T12:00:00`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(trimmed);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'number') {
    const d = new Date(value > 1e12 ? value : value * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatDateOnlyBr(value) {
  const date = toJsDate(value);
  if (!date) return '-';
  return date.toLocaleDateString('pt-BR');
}

function formatLote(lote) {
  if (lote == null || lote === '') return '-';
  if (Array.isArray(lote)) {
    const parts = lote.map((l) => String(l ?? '').trim()).filter(Boolean);
    return parts.length ? parts.join(', ') : '-';
  }
  return String(lote);
}

function dataRecebimentoAnalitico(rec) {
  return (
    rec?.data_recebimento ||
    rec?.dataRecebimento ||
    rec?.criado_em ||
    rec?.created_at ||
    null
  );
}

function labelDecisao(decisao) {
  const map = {
    aceitar: 'Aprovado',
    aprovado: 'Aprovado',
    aprovado_qualidade: 'Aprovado',
    rejeitar: 'Rejeitado',
    rejeitado: 'Rejeitado',
    rejeitado_qualidade: 'Rejeitado',
    pendente: 'Pendente'
  };
  return map[String(decisao || '').toLowerCase()] || (decisao ? String(decisao) : 'Pendente');
}

function labelStatus(status) {
  if (!status) return '-';
  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

async function loadLogoDataUrl() {
  const candidates = [
    '/logo-qualimanager-horizontal.jpeg',
    '/logo-qualimanager.jpeg',
    '/logo.png'
  ];

  for (const src of candidates) {
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/jpeg', 0.92));
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = () => reject(new Error(`Falha ao carregar ${src}`));
        img.src = `${src}?t=${Date.now()}`;
      });
      return dataUrl;
    } catch {
      // tenta próximo
    }
  }
  return null;
}

function buildKpis(reportType, reports, analytics) {
  const r = reports || {};
  const periodoTotal = analytics?.periodoTotal;

  if (reportType === 'fornecedores') {
    return [
      { label: 'Total', value: String(r.total_fornecedores || 0) },
      { label: 'Aprovados', value: String(r.distribuicao_fornecedores?.aprovado || 0) },
      { label: 'Pendentes', value: String(r.distribuicao_fornecedores?.pendente || 0) },
      { label: 'Rejeitados', value: String(r.distribuicao_fornecedores?.rejeitado || 0) }
    ];
  }

  if (reportType === 'produtos') {
    return [
      { label: 'Total', value: String(r.total_produtos || 0) },
      { label: 'Ativos', value: String(r.produtos_ativos || r.distribuicao_produtos?.ativo || 0) },
      { label: 'Inativos', value: String(r.produtos_inativos || r.distribuicao_produtos?.inativo || 0) },
      { label: 'Baixo estoque', value: String(r.produtos_baixo_estoque || r.distribuicao_produtos?.baixo_estoque || 0) }
    ];
  }

  if (reportType === 'documentos') {
    return [
      { label: 'No período', value: String(periodoTotal ?? analytics?.items?.length ?? 0) },
      { label: 'Aprovados', value: String(analytics?.counts?.aprovado ?? r.documentos_aprovados ?? 0) },
      { label: 'Pendentes', value: String(analytics?.counts?.pendente ?? r.documentos_pendentes ?? 0) },
      { label: 'Rejeitados', value: String(analytics?.counts?.rejeitado ?? r.documentos_rejeitados ?? 0) }
    ];
  }

  if (reportType === 'recebimentos') {
    return [
      { label: 'No período', value: String(periodoTotal ?? analytics?.items?.length ?? r.total_recebimentos ?? 0) },
      { label: 'Aprovados', value: String(analytics?.counts?.aprovado ?? r.distribuicao_recebimentos?.aprovado ?? 0) },
      { label: 'Pendentes', value: String(analytics?.counts?.pendente ?? r.distribuicao_recebimentos?.pendente ?? 0) },
      { label: 'Rejeitados', value: String(analytics?.counts?.rejeitado ?? r.distribuicao_recebimentos?.rejeitado ?? 0) },
      { label: 'Qualidade', value: `${(r.nota_qualidade_media || 0).toFixed(1)}/10` }
    ];
  }

  if (reportType === 'marketplace') {
    return [
      { label: 'Propostas', value: String(r.propostas_total || 0) },
      { label: 'Ativas', value: String(r.propostas_ativas || 0) },
      { label: 'Aceitas', value: String(r.propostas_aceitas || 0) },
      { label: 'Rejeitadas', value: String(r.propostas_rejeitadas || 0) }
    ];
  }

  // geral
  return [
    { label: 'Fornecedores', value: String(r.total_fornecedores || 0) },
    { label: 'Produtos', value: String(r.total_produtos || 0) },
    { label: 'Recebimentos', value: String(periodoTotal ?? r.total_recebimentos ?? 0) },
    { label: 'Qualidade', value: `${(r.nota_qualidade_media || 0).toFixed(1)}/10` },
    { label: 'Aprovados', value: String(analytics?.counts?.aprovado ?? r.distribuicao_recebimentos?.aprovado ?? 0) }
  ];
}

function buildContextSections(reportType, reports, analytics = {}) {
  const r = reports || {};
  const totaisRecebidos = formatTotaisPorUnidade(
    analytics.porUnidade ?? r.quantidade_por_unidade
  );
  const totaisAceitos = formatTotaisPorUnidade(
    analytics.porUnidadeAceita ?? r.quantidade_aceita_por_unidade
  );

  if (reportType === 'fornecedores') {
    return [
      {
        heading: 'Distribuição por status',
        lines: [
          `Aprovados: ${r.distribuicao_fornecedores?.aprovado || 0}`,
          `Pendentes: ${r.distribuicao_fornecedores?.pendente || 0}`,
          `Rejeitados: ${r.distribuicao_fornecedores?.rejeitado || 0}`
        ]
      }
    ];
  }

  if (reportType === 'produtos') {
    return [
      {
        heading: 'Distribuição do catálogo',
        lines: [
          `Ativos: ${r.distribuicao_produtos?.ativo || 0}`,
          `Inativos: ${r.distribuicao_produtos?.inativo || 0}`,
          `Baixo estoque: ${r.distribuicao_produtos?.baixo_estoque || 0}`
        ]
      }
    ];
  }

  if (reportType === 'documentos') {
    return [
      {
        heading: 'Indicadores documentais',
        lines: [
          `Total cadastrado: ${r.documentos_total || 0}`,
          `Próximos do vencimento: ${r.documentos_vencendo || 0}`,
          `Tempo médio de aprovação: ${Number(r.tempo_medio_aprovacao || 0).toFixed(1)} dias`
        ]
      }
    ];
  }

  if (reportType === 'recebimentos') {
    return [
      {
        heading: 'Indicadores de qualidade',
        lines: [
          `Nota média: ${(r.nota_qualidade_media || 0).toFixed(1)}/10`,
          `Conformidade média: ${(r.percentual_conformidade || 0).toFixed(1)}%`
        ]
      },
      {
        heading: 'Quantidades por unidade (período)',
        lines: [
          `Recebidas: ${totaisRecebidos}`,
          `Aceitas: ${totaisAceitos}`
        ]
      }
    ];
  }

  if (reportType === 'marketplace') {
    return [
      {
        heading: 'Resumo do marketplace',
        lines: [
          `Propostas totais: ${r.propostas_total || 0}`,
          `Ativas: ${r.propostas_ativas || 0}`,
          `Aceitas: ${r.propostas_aceitas || 0}`,
          `Rejeitadas: ${r.propostas_rejeitadas || 0}`
        ]
      }
    ];
  }

  return [
    {
      heading: 'Resumo executivo',
      lines: [
        `Fornecedores: ${r.total_fornecedores || 0} (aprovados: ${r.distribuicao_fornecedores?.aprovado || 0})`,
        `Produtos: ${r.total_produtos || 0} (ativos: ${r.produtos_ativos || 0})`,
        `Recebimentos no período: ${analytics.periodoTotal ?? r.total_recebimentos ?? 0}`,
        `Qualidade média: ${(r.nota_qualidade_media || 0).toFixed(1)}/10 (${(r.percentual_conformidade || 0).toFixed(1)}% conformidade)`
      ]
    },
    {
      heading: 'Quantidades por unidade (período)',
      lines: [
        `Recebidas: ${totaisRecebidos}`,
        `Aceitas: ${totaisAceitos}`
      ]
    },
    {
      heading: 'Distribuição dos recebimentos',
      lines: [
        `Aprovados: ${r.distribuicao_recebimentos?.aprovado || 0}`,
        `Pendentes: ${r.distribuicao_recebimentos?.pendente || 0}`,
        `Rejeitados: ${r.distribuicao_recebimentos?.rejeitado || 0}`
      ]
    }
  ];
}

function getTableConfig(reportType, analytics) {
  const items = analytics?.items || [];
  if (!items.length) return null;

  if (reportType === 'documentos') {
    return {
      title: 'Documentos do período',
      head: [['Documento', 'Tipo', 'Status', 'Upload', 'Fornecedor']],
      body: items.map((d) => [
        d.nome || d.titulo || '-',
        d.tipo || '-',
        labelStatus(d.status),
        formatDateOnlyBr(d.data_upload || d.criado_em),
        d.nome_fornecedor || d.fornecedor || '-'
      ])
    };
  }

  if (reportType === 'fornecedores') {
    return {
      title: 'Fornecedores (amostra analítica)',
      head: [['Empresa', 'Status', 'Categoria', 'Cadastro']],
      body: items.map((s) => [
        s.nome_empresa || s.razao_social || s.nome || '-',
        labelStatus(s.status),
        s.categoria || '-',
        formatDateOnlyBr(s.criado_em || s.data_cadastro)
      ])
    };
  }

  if (reportType === 'produtos') {
    return {
      title: 'Produtos do catálogo',
      head: [['Produto', 'Código', 'Categoria', 'Status']],
      body: items.map((p) => [
        p.nome || p.produto_nome || '-',
        p.codigo || p.sku || '-',
        p.categoria || '-',
        p.ativo === false ? 'Inativo' : 'Ativo'
      ])
    };
  }

  if (reportType === 'marketplace') {
    return {
      title: 'Propostas do marketplace',
      head: [['Proposta', 'Status', 'Fornecedor', 'Atualização']],
      body: items.map((p) => [
        p.titulo || p.nome || p.id || '-',
        labelStatus(p.status),
        p.fornecedor || p.nome_fornecedor || '-',
        formatDateOnlyBr(p.atualizado_em || p.criado_em)
      ])
    };
  }

  // geral / recebimentos
  // Larguras em mm (A4 útil ~182mm com margens 14)
  return {
    title: 'Recebimentos do período',
    head: [['Data', 'Produto', 'Fornecedor', 'NF', 'Lote', 'Quantidade', 'Decisão']],
    body: items.map((rec) => [
      formatDateOnlyBr(dataRecebimentoAnalitico(rec)),
      rec.produto_nome || rec.produto || '-',
      rec.fornecedor || rec.nome_fornecedor || '-',
      rec.numero_nota_fiscal || rec.nota_fiscal || rec.nf || '-',
      formatLote(rec.lote_fornecedor || rec.lote),
      formatQuantidadeComUnidade(
        rec.quantidade_recebida ?? rec.quantidade ?? 0,
        rec.unidade_medida || rec.unidade || ''
      ),
      labelDecisao(rec.decisao_final)
    ]),
    columnStyles: {
      0: { cellWidth: 20 }, // Data
      1: { cellWidth: 36 }, // Produto
      2: { cellWidth: 28 }, // Fornecedor (reduzido)
      3: { cellWidth: 22 }, // NF
      4: { cellWidth: 20 }, // Lote
      5: { cellWidth: 30 }, // Quantidade + unidade
      6: { cellWidth: 22 }  // Decisão
    }
  };
}

function getQuantidadePorUnidadeTable(analytics, reports) {
  const recebidas = toListaPorUnidade(
    analytics?.porUnidade ?? reports?.quantidade_por_unidade
  );
  const aceitasMap = Object.fromEntries(
    toListaPorUnidade(
      analytics?.porUnidadeAceita ?? reports?.quantidade_aceita_por_unidade
    ).map((row) => [row.unidade, row.quantidade])
  );
  if (!recebidas.length) return null;
  return {
    title: 'Totais por unidade de medida',
    head: [['Unidade', 'Recebida', 'Aceita']],
    body: recebidas.map((row) => [
      row.unidade,
      formatQuantidadeComUnidade(row.quantidade, row.unidade),
      formatQuantidadeComUnidade(aceitasMap[row.unidade] || 0, row.unidade)
    ])
  };
}

/**
 * Gera PDF profissional sensível ao tipo de relatório.
 */
export async function generateProfessionalReportPdf({
  reportType = 'geral',
  empresa = 'Empresa',
  dateRange = {},
  reports = {},
  analytics = {},
  geradoPor = ''
}) {
  const meta = REPORT_META[reportType] || REPORT_META.geral;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 14;
  let y = 12;

  const logo = await loadLogoDataUrl();

  // Header band
  doc.setFillColor(...BRAND.navy);
  doc.rect(0, 0, pageWidth, 28, 'F');
  doc.setFillColor(...BRAND.blue);
  doc.rect(0, 28, pageWidth, 1.5, 'F');

  if (logo) {
    try {
      doc.addImage(logo, 'JPEG', marginX, 4, 42, 20);
    } catch {
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('QualiManager', marginX, 16);
    }
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('QualiManager', marginX, 16);
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Consultoria Regulatória', pageWidth - marginX, 12, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Sistema de Qualidade e Rastreabilidade', pageWidth - marginX, 18, { align: 'right' });

  y = 38;

  doc.setTextColor(...BRAND.navy);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(meta.title, marginX, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.gray);
  doc.text(meta.subtitle, marginX, y);
  y += 8;

  // Meta box
  doc.setFillColor(...BRAND.light);
  doc.setDrawColor(...BRAND.line);
  doc.roundedRect(marginX, y, pageWidth - marginX * 2, 18, 2, 2, 'FD');

  doc.setTextColor(...BRAND.dark);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Empresa', marginX + 3, y + 6);
  doc.text('Período', marginX + 70, y + 6);
  doc.text('Gerado em', marginX + 130, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.text(String(empresa || 'Empresa').slice(0, 36), marginX + 3, y + 12);
  doc.text(
    `${formatDateBr(dateRange.start)} a ${formatDateBr(dateRange.end)}`,
    marginX + 70,
    y + 12
  );
  doc.text(new Date().toLocaleString('pt-BR'), marginX + 130, y + 12);
  y += 26;

  // KPIs
  const kpis = buildKpis(reportType, reports, analytics);
  const cols = Math.min(kpis.length, 3);
  const gap = 3;
  const boxW = (pageWidth - marginX * 2 - gap * (cols - 1)) / cols;
  const boxH = 16;

  kpis.forEach((kpi, idx) => {
    if (idx > 0 && idx % cols === 0) y += boxH + gap;
    const col = idx % cols;
    const x = marginX + col * (boxW + gap);

    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...BRAND.blue);
    doc.roundedRect(x, y, boxW, boxH, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.gray);
    doc.text(kpi.label, x + 3, y + 5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...BRAND.navy);
    doc.text(String(kpi.value), x + 3, y + 12);
  });
  y += boxH + 10;

  // Context sections
  const sections = buildContextSections(reportType, reports, analytics);
  sections.forEach((section) => {
    if (y > pageHeight - 40) {
      doc.addPage();
      y = 20;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...BRAND.navy);
    doc.text(section.heading, marginX, y);
    y += 2;
    doc.setDrawColor(...BRAND.blue);
    doc.setLineWidth(0.4);
    doc.line(marginX, y, marginX + 40, y);
    y += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.dark);
    section.lines.forEach((line) => {
      if (y > pageHeight - 20) {
        doc.addPage();
        y = 20;
      }
      doc.text(`•  ${line}`, marginX + 1, y);
      y += 5;
    });
    y += 4;
  });

  const drawTableBlock = (table) => {
    if (!table) return;
    if (y > pageHeight - 50) {
      doc.addPage();
      y = 20;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...BRAND.navy);
    doc.text(table.title, marginX, y);
    y += 2;
    doc.setDrawColor(...BRAND.blue);
    doc.line(marginX, y, marginX + 50, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      head: table.head,
      body: table.body,
      theme: 'striped',
      styles: {
        font: 'helvetica',
        fontSize: 7.5,
        cellPadding: 1.5,
        textColor: BRAND.dark,
        lineColor: BRAND.line,
        lineWidth: 0.1,
        overflow: 'linebreak'
      },
      headStyles: {
        fillColor: BRAND.navy,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'left',
        fontSize: 7.5
      },
      alternateRowStyles: {
        fillColor: BRAND.light
      },
      columnStyles: table.columnStyles || {},
      margin: { left: marginX, right: marginX },
      didDrawPage: (data) => {
        const pageCurrent = data.pageNumber;
        doc.setFontSize(7);
        doc.setTextColor(...BRAND.gray);
        doc.text(
          `QualiManager — ${meta.title}${geradoPor ? ` | ${geradoPor}` : ''}`,
          marginX,
          pageHeight - 8
        );
        doc.text(
          `Página ${pageCurrent}`,
          pageWidth - marginX,
          pageHeight - 8,
          { align: 'right' }
        );
      }
    });
    y = (doc.lastAutoTable?.finalY || y) + 10;
  };

  // Totais por unidade (quando aplicável)
  if (reportType === 'geral' || reportType === 'recebimentos') {
    drawTableBlock(getQuantidadePorUnidadeTable(analytics, reports));
  }

  // Analytical table
  const table = getTableConfig(reportType, analytics);
  if (table) {
    drawTableBlock(table);
  } else if (!(reportType === 'geral' || reportType === 'recebimentos')) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.gray);
    doc.text('Nenhum item analítico encontrado para o período selecionado.', marginX, y + 4);

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(...BRAND.gray);
      doc.text(`QualiManager — ${meta.title}`, marginX, pageHeight - 8);
      doc.text(`Página ${i} de ${pageCount}`, pageWidth - marginX, pageHeight - 8, { align: 'right' });
    }
  }

  // Ensure footer on all pages when table drew some
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    doc.setDrawColor(...BRAND.line);
    doc.line(marginX, pageHeight - 12, pageWidth - marginX, pageHeight - 12);
  }

  const fileName = `relatorio_${reportType}_${dateRange.start || 'inicio'}_${dateRange.end || 'fim'}.pdf`;
  doc.save(fileName);
  return fileName;
}

export { REPORT_META, formatDateBr, labelDecisao };
